import React, { useState, useRef, useCallback } from 'react';
import { LuStar } from 'react-icons/lu';

const playPlimSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;

    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(1200, now);
    osc1.frequency.exponentialRampToValueAtTime(1800, now + 0.08);
    gain1.gain.setValueAtTime(0.15, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.25);

    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(2400, now + 0.06);
    gain2.gain.setValueAtTime(0.08, now + 0.06);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.06);
    osc2.stop(now + 0.3);

    setTimeout(() => ctx.close(), 500);
  } catch (e) {}
};

const SPARKLE_COUNT = 8;

function StarRating({ rating = 0, onRate, size = 16, readOnly = false }) {
  const [hoverRating, setHoverRating] = useState(0);
  const [sparkles, setSparkles] = useState([]);
  const containerRef = useRef(null);
  const sparkleIdRef = useRef(0);

  const spawnSparkles = useCallback((e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;

    const newSparkles = Array.from({ length: SPARKLE_COUNT }, (_, i) => {
      const angle = (Math.PI * 2 * i) / SPARKLE_COUNT + (Math.random() - 0.5) * 0.5;
      const distance = 25 + Math.random() * 30;
      return {
        id: ++sparkleIdRef.current,
        x: cx,
        y: cy,
        tx: Math.cos(angle) * distance,
        ty: Math.sin(angle) * distance,
        size: 2 + Math.random() * 3,
        delay: Math.random() * 80,
      };
    });

    setSparkles(newSparkles);
    setTimeout(() => setSparkles([]), 1200);
  }, []);

  const handleClick = useCallback((value, e) => {
    e.stopPropagation();
    if (readOnly || !onRate) return;
    const newRating = value === rating ? 0 : value;
    onRate(newRating);

    if (newRating > 0) {
      playPlimSound();
      spawnSparkles(e.nativeEvent);
    }
  }, [rating, readOnly, onRate, spawnSparkles]);

  const handleMouseEnter = (value) => {
    if (readOnly) return;
    setHoverRating(value);
  };

  const handleMouseLeave = () => {
    if (readOnly) return;
    setHoverRating(0);
  };

  const displayRating = hoverRating || rating;

  return (
    <div
      ref={containerRef}
      className={`star-rating ${readOnly ? 'star-rating--readonly' : ''}`}
      onMouseLeave={handleMouseLeave}
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const fullActive = displayRating >= star;
        const halfActive = !fullActive && displayRating >= star - 0.5;

        return (
          <span
            key={star}
            className="star-rating__star"
            style={{ fontSize: size }}
          >
            <LuStar className="star-rating__star-bg" />

            {fullActive && (
              <LuStar className="star-rating__star-fg" fill="currentColor" />
            )}
            {halfActive && (
              <LuStar
                className="star-rating__star-fg"
                fill="currentColor"
                style={{ clipPath: 'inset(0 50% 0 0)' }}
              />
            )}

            {!readOnly && (
              <span
                className="star-rating__star-half-left"
                onMouseEnter={() => handleMouseEnter(star - 0.5)}
                onClick={(e) => handleClick(star - 0.5, e)}
              />
            )}
            {!readOnly && (
              <span
                className="star-rating__star-half-right"
                onMouseEnter={() => handleMouseEnter(star)}
                onClick={(e) => handleClick(star, e)}
              />
            )}
          </span>
        );
      })}

      {/* Sparkle particles */}
      {sparkles.map((s) => (
        <span
          key={s.id}
          className="star-rating__sparkle"
          style={{
            left: s.x,
            top: s.y,
            width: s.size,
            height: s.size,
            '--tx': `${s.tx}px`,
            '--ty': `${s.ty}px`,
            animationDelay: `${s.delay}ms`,
          }}
        />
      ))}
    </div>
  );
}

export default StarRating;
